import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { insuranceService } from '../services/insuranceService';
import { entitySyncService } from '../services/entitySyncService';

// --- Core Async Thunks ---
export const fetchInsurances = createAsyncThunk(
  'insurances/fetchInsurances',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await insuranceService.fetchInsurances(params);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const loadInsurancesFromCache = createAsyncThunk(
  'insurances/loadFromCache',
  async (_, { rejectWithValue }) => {
    try {
      const data = await entitySyncService.getLocalEntities('insurances');
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchInsuranceById = createAsyncThunk(
  'insurances/fetchInsuranceById',
  async (payload, { rejectWithValue }) => {
    const id = typeof payload === 'object' ? payload.id : payload;
    try {
      const response = await insuranceService.getInsuranceById(id);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    condition: (payload, { getState }) => {
      if (typeof payload === 'object' && payload.forceRefresh) return true;

      const id = typeof payload === 'object' ? payload.id : payload;
      const { insurances } = getState();

      const hasFullData = insurances.selectedInsurance?.name || insurances.selectedInsurance?.firmName;
      const currentId = insurances.selectedInsurance?.id || insurances.selectedInsurance?.insuranceId;

      // Only skip if the ID matches AND we actually have the name/details
      if (insurances.loading || (currentId === id && hasFullData && !insurances.error)) {
        return false;
      }
    }
  }
);



export const createInsurance = createAsyncThunk(
  'insurances/createInsurance',
  async (insuranceData, { rejectWithValue }) => {
    try {
      const response = await insuranceService.createInsurance(insuranceData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateInsurance = createAsyncThunk(
  'insurances/updateInsurance',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await insuranceService.updateInsurance(id, data);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteInsurance = createAsyncThunk(
  'insurances/deleteInsurance',
  async (id, { rejectWithValue }) => {
    try {
      await insuranceService.deleteInsurance(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const mergeInsurances = createAsyncThunk(
  'insurances/mergeInsurances',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await insuranceService.mergeInsurances(payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// --- Modal-Specific Thunks ---
export const searchInsurances = createAsyncThunk(
  'insurances/searchInsurances',
  async (params, { rejectWithValue }) => {
    try {
      const response = await insuranceService.fetchInsurances(params);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchContactsForSelection = createAsyncThunk(
  'insurances/fetchContactsForSelection',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await insuranceService.fetchInsurances(filters);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


const insuranceSlice = createSlice({
  name: 'insurances',
  initialState: {
    insurances: [],
    selectedInsurance: null,
    loading: false,
    error: null,
    pagination: null,

    // State for search results in AddInsuranceOrContactModal
    searchResults: [],
    searchLoading: false,
    searchError: null,

    // State for the InsuranceSelectionModal contact list
    contactList: [],
    contactListLoading: false,
    contactListError: null,
    contactPagination: null,

    // State for CUD operations
    actionLoading: false,
    actionError: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.actionError = null;
      state.searchError = null;
      state.contactListError = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearSelectedInsurance: (state) => {
      state.selectedInsurance = null;
    },
    clearContactList: (state) => {
      state.contactList = [];
      state.contactPagination = null;
      state.contactListError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Main list fetching
      .addCase(fetchInsurances.pending, (state) => { state.loading = true; })
      .addCase(fetchInsurances.fulfilled, (state, action) => {
        state.loading = false;
        state.insurances = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchInsurances.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(loadInsurancesFromCache.fulfilled, (state, action) => {
        state.insurances = action.payload;
      })

      // Fetch by ID
      .addCase(fetchInsuranceById.pending, (state) => { state.loading = true; })
      .addCase(fetchInsuranceById.fulfilled, (state, action) => { state.loading = false; state.selectedInsurance = action.payload; })
      .addCase(fetchInsuranceById.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Modal company search
      .addCase(searchInsurances.pending, (state) => { state.searchLoading = true; })
      .addCase(searchInsurances.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchInsurances.rejected, (state, action) => { state.searchLoading = false; state.searchError = action.payload; })

      // Contact list for selection modal
      .addCase(fetchContactsForSelection.pending, (state) => { state.contactListLoading = true; })
      .addCase(fetchContactsForSelection.fulfilled, (state, action) => {
        state.contactListLoading = false;
        // Flatten contacts from companies, adding a reference to the parent company
        // Only include contacts that are of type 'billing'
        const contacts = (action.payload.data || []).flatMap(company =>
          (company.contacts || [])
            .filter(contact => contact && contact.type === 'billing')
            .map(contact => ({
              ...contact,
              insurance: { id: company.id, name: company.name }
            }))
        );
        state.contactList = contacts;
        state.contactPagination = action.payload.pagination || null;
      })
      .addCase(fetchContactsForSelection.rejected, (state, action) => { state.contactListLoading = false; state.contactListError = action.payload; })

      // Generic handlers for create, update, delete actions
      .addMatcher(
        (action) => [createInsurance.pending, updateInsurance.pending, deleteInsurance.pending, mergeInsurances.pending].includes(action.type),
        (state) => { state.actionLoading = true; state.actionError = null; }
      )
      .addMatcher(
        (action) => [createInsurance.fulfilled, updateInsurance.fulfilled, deleteInsurance.fulfilled, mergeInsurances.fulfilled].includes(action.type),
        (state, action) => {
          state.actionLoading = false;
          if (action.type === updateInsurance.fulfilled.type && state.selectedInsurance?.id === action.payload.id) {
            state.selectedInsurance = action.payload;
          }

          // Mirror to IndexedDB
          if (action.type === createInsurance.fulfilled.type || action.type === updateInsurance.fulfilled.type) {
            entitySyncService.upsertLocalEntity('insurances', action.payload);
          } else if (action.type === deleteInsurance.fulfilled.type) {
            entitySyncService.deleteLocalEntity('insurances', action.payload);
          }
        }
      )
      .addMatcher(
        (action) => [createInsurance.rejected, updateInsurance.rejected, deleteInsurance.rejected, mergeInsurances.rejected].includes(action.type),
        (state, action) => { state.actionLoading = false; state.actionError = action.payload; }
      );
  },
});

export const {
  clearError,
  clearSearchResults,
  clearSelectedInsurance,
  clearContactList,
} = insuranceSlice.actions;

export default insuranceSlice.reducer;
